import { useState, useCallback } from 'react';
import styled from 'styled-components';
import { UseFormSetValue } from 'react-hook-form';
import { RawFormFillerData } from 'shared-utils/types/formFiller';
import { WithOptionalTooltip } from './WithOptionalTooltip';
import { SignatureModal } from '../SignatureModal';
import { FieldState } from '../../../lib/formFiller';
import { useFormFillerSignature } from '../hooks';
import SignaturePenIcon from 'design-system/src/assets/icons/signaturePen.svg?react';
import { SignatureContainer } from '../styles';
import { getTabIndex } from '../../../lib/formFiller';

const SignaturePreview = styled.img`
  width: 100%;
  height: 70%;
  object-fit: contain;
  object-position: center;
  background-color: transparent;
`;

const PlaceholderContainer = styled.div`
  position: absolute;
  text-align: center;
  pointer-events: none;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  gap: 8px;
  width: 100%;
`;

const InnerSignatureContainer = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  outline: none;
`;
/**
 * Signature input field component that opens a modal for signature creation
 */
export function SignatureFieldInput({
  fieldState,
  setValue,
  coordinates,
  signedFields,
  value,
}: {
  fieldState: FieldState;
  setValue: UseFormSetValue<RawFormFillerData>;
  coordinates: { width: number; height: number };
  signedFields: string[];
  value: string | null | undefined;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Determine if we have a signature to display
  const hasSignature = value !== null && value !== undefined && value !== '';
  const { getSignatureImageUrl, allowSignatureUpload, setSignature } = useFormFillerSignature();
  const signatureUrl = getSignatureImageUrl(fieldState.role);
  const handleClick = useCallback(() => {
    if (fieldState.isReadOnly) return;
    if (hasSignature && signatureUrl) {
      // Clear the signature
      setValue(fieldState.id, '', {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      if (signedFields.length === 1 && signedFields[0] === fieldState.id) {
        // If this is the only signed field, clear the signature immediately
        setSignature(null);
      }
      return;
    }

    // If we have a session signature, use it immediately
    if (signatureUrl) {
      setValue(fieldState.id, 'signed', {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      return;
    }

    // Otherwise, open the modal to create/edit signature
    setIsModalOpen(true);
  }, [fieldState.isReadOnly, signatureUrl, hasSignature, setValue, fieldState.id, signedFields, setSignature]);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleSignatureSubmitted = useCallback(() => {
    setIsModalOpen(false);
    setValue(fieldState.id, 'signed', {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [setValue, fieldState.id]);

  return (
    <>
      <SignatureContainer
        $isUnassigned={!fieldState.isAssigned}
        $isEmpty={fieldState.isEmpty}
        $isRequired={fieldState.isRequired}
        onClick={handleClick}
        disabled={fieldState.isReadOnly}
        tabIndex={getTabIndex(fieldState)}
      >
        <WithOptionalTooltip fieldState={fieldState}>
          <InnerSignatureContainer>
            {signatureUrl && hasSignature ? (
              <SignaturePreview src={signatureUrl} alt="Signature" />
            ) : (
              <PlaceholderContainer>
                <SignaturePenIcon aria-hidden />
                {fieldState.isReadOnly ? 'Signature required' : 'Signature'}
              </PlaceholderContainer>
            )}
          </InnerSignatureContainer>
        </WithOptionalTooltip>
      </SignatureContainer>

      <SignatureModal
        width={coordinates.width}
        height={coordinates.height}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSignatureSubmitted={handleSignatureSubmitted}
        allowUpload={allowSignatureUpload}
      />
    </>
  );
}

export default SignatureFieldInput;
